import { CopyOutlined, SearchOutlined } from '@ant-design/icons'
import {
  Button,
  Form,
  Modal,
  Row,
  Select,
  Space,
  Table,
  message,
  Input,
  Typography
} from 'antd'
import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { PageHeader } from '~/components/PageHeader'
import { userApi } from '~/api'
import { LocalDateTime } from '~/components/LocalDateTime'
import { CaseDownloadButton } from '~/components/CaseDownloadButton'
import { useTableDataSourceKey } from '~/hooks/useTableDataSourceKey'
import { useCaseQuery } from '~/pages/UploadFiles/query'
import {
  clipboard,
  compareDate,
  compareNumber,
  compareText,
  formatBytes,
  showErrors
} from '~/utils/helpers'

const columns = ({ courseId, copyS3Command, copyLoading, copyingCaseId }) => [
  {
    title: 'Id',
    dataIndex: 'id'
  },
  {
    title: 'Name',
    dataIndex: 'name',
    sorter: (a, b) => compareText(a.name, b.name)
  },
  {
    title: 'Upload Location',
    render: (cases) =>
      cases.upload_location
        ? `${cases.upload_location.name} (${cases.upload_location.region})`
        : ''
  },
  {
    title: 'Size',
    render: (cases) => formatBytes(cases.size),
    sorter: (a, b) => compareNumber(a.size, b.size)
  },
  {
    title: 'Created At',
    render: (cases) => <LocalDateTime date={cases.created_at} />,
    sorter: (a, b) => compareDate(a.created_at, b.created_at)
  },
  {
    title: 'Uploader',
    render: (cases) => cases.user?.name,
    sorter: (a, b) => compareText(a.user.name, b.user.name)
  },
  {
    title: 'Action',
    render: (cases) => (
      <Space>
        <Button
          size='small'
          type='primary'
          icon={<CopyOutlined />}
          loading={copyLoading && copyingCaseId === cases.id}
          onClick={() => copyS3Command(cases.id)}
        >
          Copy command
        </Button>
        <CaseDownloadButton courseId={courseId} caseId={cases.id} />
      </Space>
    )
  }
]

const toDateOptionTime = (date) => {
  const [day, month, year] = date.split('-')

  return new Date(Number(year), Number(month) - 1, Number(day)).getTime()
}

const formatDateOption = (date) =>
  new Date(date)
    .toLocaleDateString('en-GB', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      timeZone: 'UTC'
    })
    .replace(/\//g, '-')

const UploadedCases = () => {
  const { id } = useParams()
  const { data: uploadedCases, isLoading } = useCaseQuery(id)
  const dataSource = useTableDataSourceKey(uploadedCases)
  const [filterInput, setFilterInput] = useState('')
  const [copyFolderModalOpen, setCopyFolderModalOpen] = useState(false)
  const [copyFolderForm] = Form.useForm()
  const {
    mutate: copyS3Command,
    isLoading: copyLoading,
    variables: copyingCaseId
  } = useMutation(
    async (caseId) => await userApi.getCaseCopyCommand(id, caseId),
    {
      onSuccess: ({ data }) => {
        clipboard(data.command)
        message.success('Copied')
      },
      onError: showErrors
    }
  )
  const { mutate: generateCopyFolderCommand, isLoading: copyFolderLoading } =
    useMutation(
      async ({ date }) => await userApi.getCasesCopyFolderCommand(id, date),
      {
        onSuccess: ({ data }) => {
          clipboard(data.command)
          message.success('Copied')
          setCopyFolderModalOpen(false)
        },
        onError: showErrors
      }
    )

  const totalSize = useMemo(() => {
    if (!dataSource) {
      return 0
    }

    const result = dataSource.reduce((carry, item) => {
      return carry + item.size
    }, 0)

    return formatBytes(result)
  }, [dataSource])

  const dateOptions = useMemo(() => {
    if (!dataSource) {
      return []
    }

    const dates = dataSource.reduce((carry, cases) => {
      if (cases.created_at) {
        carry.add(formatDateOption(cases.created_at))
      }

      return carry
    }, new Set())

    return [...dates]
      .sort((a, b) => toDateOptionTime(b) - toDateOptionTime(a))
      .map((date) => ({
        label: date,
        value: date
      }))
  }, [dataSource])

  const filterData = useMemo(() => {
    const filter = filterInput.toLowerCase()
    if (!dataSource) {
      return []
    }

    return dataSource.filter((cases) =>
      cases.name.toLowerCase().includes(filter)
    )
  }, [dataSource, filterInput])

  const openCopyFolderModal = () => {
    copyFolderForm.resetFields()
    setCopyFolderModalOpen(true)
  }

  const handleCopyFolderCommand = (values) => {
    generateCopyFolderCommand(values)
  }

  return (
    <>
      <PageHeader
        className='radup-page-header'
        extra={[
          <Input.Search
            key='search'
            placeholder='Search'
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={setFilterInput}
          />,
          <Button
            key='copy-folder-command'
            type='primary'
            icon={<CopyOutlined />}
            onClick={openCopyFolderModal}
          >
            Copy folder command
          </Button>
        ]}
      />
      <Modal
        title='Copy folder command'
        open={copyFolderModalOpen}
        confirmLoading={copyFolderLoading}
        okText='Submit'
        onOk={() => copyFolderForm.submit()}
        onCancel={() => setCopyFolderModalOpen(false)}
      >
        <Form
          form={copyFolderForm}
          layout='vertical'
          onFinish={handleCopyFolderCommand}
        >
          <Form.Item
            label='Created date'
            name='date'
            rules={[
              {
                required: true,
                message: 'Please select a created date'
              }
            ]}
          >
            <Select
              placeholder='Select date'
              options={dateOptions}
              disabled={dateOptions.length === 0}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Typography.Title level={5} style={{ marginTop: '16px' }}>
        Files ({totalSize})
      </Typography.Title>
      <Table
        size='small'
        tableLayout='auto'
        dataSource={filterData}
        columns={columns({ courseId: id, copyS3Command, copyLoading, copyingCaseId })}
        loading={isLoading}
        sticky
        pagination={false}
        scroll={{ y: 600, x: '100%' }}
        footer={() => (
          <Row justify='start'>
            <span>Total: {filterData.length} items</span>
          </Row>
        )}
      />
    </>
  )
}

export default UploadedCases
